/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { QSTypography } from '../../../../../../shared/components/atoms';
import { getDayFromTimestamp } from '../../../../../../shared/utils/get-day-from-timestamp';
import { formatSearchText } from '../../../../../../shared/utils/text-helper';
import { chatListAtom } from '../../../states/chat-list';
import { externalSelectedChat, selectedChatAtom, selectedChatIdSetter } from '../../../states/selected-chat';
import { goToMessageAtom } from '../../../states/chat';
import { ChatProviderType, ChatType, MessageOrigin, SearchChatMessage } from '../chat-row/typings';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useCustomNavigate } from '../../../../../../shared/hooks/use-conversation-navigate';
import { urlReplacer } from '../../../../../../shared/utils/url-helpers';
import { ROUTES } from '../../../../../../shared/constants/routes';
import { isEmbedAtom } from '../../../../../embed/state';
import { MessageIcon } from '../../../../../../shared/utils/message-icon';
import formatText from '../../../../../../shared/utils/text-transformer';
import ChatAnalyticsTick from '../../../../../../shared/icons/ChatAnalyticsTick';
import SingleTickIcon from '../../../../../../shared/icons/SingleTick';
import ChatAnalyticsError from '../../../../../../shared/icons/ChatAnalyticsError';
import ConnectedIntegrationIcon from '../../../../../../shared/icons/ConnectedIntegration';
import { TextEllipsisStyles } from '../../../../../../shared/components/emotion';
import { useMaskedIdentity } from '../../../../../../shared/hooks/use-masked-identity';

interface SearchMessageRowProps {
  data: SearchChatMessage;
  searchTerm?: string;
}

const SearchMessageRow = ({ data, searchTerm: searchTermProp }: SearchMessageRowProps) => {
  const { searchTerm: globalSearchTerm } = useAtomValue(chatListAtom);
  const searchTerm = searchTermProp ?? globalSearchTerm;
  const location = useLocation();
  const navigate = useCustomNavigate();
  const isEmbed = useAtomValue(isEmbedAtom);

  const {
    message,
    messageOriginType,
    messageTime,
    messageType,
    name,
    id,
    chatMessageId,
    assignedUserId,
    assignedUserName,
    chatType,
    chatTypeId,
    phoneNumber,
    imageUrl,
    tags,
    isDone,
    integrationId,
    integrationWabaNumber,
    integrationWabaPhoneName,
    integrationDisplayName,
    readCount,
    deliveryCount,
    erroredCount,
    senderUserName,
    memberCount,
    providerType,
  } = data;

  const { nameToRender, hasAccessToReadPhone } = useMaskedIdentity({
    name: name ?? undefined,
    phoneNumber,
  });

  const messageStatusIcon = useMemo(() => {
    if (!message || messageOriginType !== MessageOrigin.USER) {
      return null;
    }
    if (erroredCount) {
      return (
        <ChatAnalyticsError
          css={css`
            height: 16px;
            width: 16px;
          `}
        />
      );
    }
    if (deliveryCount || readCount) {
      return (
        <ChatAnalyticsTick
          css={css(
            `
              height: 16px;
              width: 16px;
            `,
            css(readCount ? `fill: #009de2;` : `fill: #888888;`)
          )}
        />
      );
    }
    return (
      <SingleTickIcon
        css={css`
          height: 16px;
          width: 16px;
          fill: #888888;
        `}
      />
    );
  }, [messageOriginType]);

  const parsedMessage = useMemo(() => formatSearchText(message, searchTerm), [message, searchTerm]);

  const setSelectedChatId = useSetAtom(selectedChatIdSetter);
  const currentSelectedChat = useAtomValue(selectedChatAtom);
  const [externalSelectedState, setExternalSelectedChat] = useAtom(externalSelectedChat);
  const setGoToMessage = useSetAtom(goToMessageAtom);

  const onChatClick = () => {
    setGoToMessage({ messageId: chatMessageId, messageTime: messageTime });
    setSelectedChatId('');

    const baseChatData = currentSelectedChat || externalSelectedState;
    const baseChatDataFromSearch = {
      name: name || '',
      id,
      unreadCount: 0,
      assignedUserId,
      assignedUserName,
      chatType,
      chatTypeId,
      phoneNumber,
      imageUrl,
      tags,
      isDone,
      selected: true,
      integrationId,
      integrationWabaNumber,
      integrationWabaPhoneName,
      integrationDisplayName,
      providerType,
      isSlaActive: baseChatData?.isSlaActive ?? false,
      slaDueDate: baseChatData?.slaDueDate,
      slaBreachLevel: baseChatData?.slaBreachLevel,
    };

    if (searchTermProp && baseChatData) {
      // When clicking from chat-message-search, preserve all data from current selectedChat
      // and only update goToMessageId and goToMessageTime
      setExternalSelectedChat({
        ...baseChatData,
        selected: true,
        id: id ?? baseChatData.id,
        assignedUserId: assignedUserId ?? baseChatData.assignedUserId,
        assignedUserName: assignedUserName ?? baseChatData.assignedUserName,
        tags: tags ?? baseChatData.tags,
        isDone: isDone ?? baseChatData.isDone,
        integrationId: integrationId ?? baseChatData.integrationId,
        integrationWabaNumber: integrationWabaNumber ?? baseChatData.integrationWabaNumber,
        integrationWabaPhoneName: integrationWabaPhoneName ?? baseChatData.integrationWabaPhoneName,
        integrationDisplayName: integrationDisplayName ?? baseChatData.integrationDisplayName,
        imageUrl: imageUrl ?? baseChatData.imageUrl,
      });
    } else {
      // Use data from search result (for both chat-message-search fallback and global search)
      if (chatType === ChatType.WA_GROUP) {
        // TODO: WA_GROUP handling is remaining for message search
        setExternalSelectedChat({
          ...baseChatDataFromSearch,
          chatType: ChatType.WA_GROUP,
          memberCount: memberCount ?? 0,
          createdBy: { id: '', name: '' },
          groupChatDescription: '',
        });
      } else if (chatType === ChatType.INDIVIDUAL) {
        setExternalSelectedChat({
          ...baseChatDataFromSearch,
          chatType: ChatType.INDIVIDUAL,
        });

        const conversationId =
          providerType === ChatProviderType.INSTAGRAM ? chatTypeId : hasAccessToReadPhone ? phoneNumber : chatTypeId;
        const pathname = urlReplacer(
          `${isEmbed ? ROUTES.EMBED.replace('/*', '') : ''}${ROUTES.CONVERSATIONS}/:integrationWabaNumber/:id`,
          [
            [':id', conversationId],
            [':integrationWabaNumber', integrationWabaNumber],
          ]
        );
        navigate({ pathname, search: location.search });
      }
    }
  };

  return (
    <ButtonBase
      sx={{
        display: 'flex',
        padding: '12px 16px',
        width: '100%',
        height: 'fit-content',
        maxHeight: 'max-content',
        background: 'white',
        cursor: 'pointer',
        overflow: 'hidden',
        '&:hover': {
          background: '#FBFBFC',
        },
      }}
      onClick={onChatClick}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flex: '1',
          paddingLeft: '16px',
          minWidth: '0',
          minHeight: '0',
          width: '100%',
          gap: '4px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <QSTypography
            sx={{
              fontSize: '16px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: 'inherit',
              flex: '1',
              textAlign: 'initial',
            }}
          >
            {!searchTermProp ? nameToRender : senderUserName || 'Customer'}
          </QSTypography>
        </Box>
        {!searchTermProp &&
        chatType &&
        [ChatType.INDIVIDUAL, ChatType.WA_GROUP].includes(chatType) &&
        ((typeof integrationDisplayName === 'string' && integrationDisplayName.length > 0) ||
          (typeof integrationWabaPhoneName === 'string' && integrationWabaPhoneName.length > 0)) ? (
          <Box
            css={css`
              display: flex;
              align-items: center;
              justify-content: flex-start;
              column-gap: 6px;
              width: 100%;
            `}
          >
            <ConnectedIntegrationIcon color="#585858" />
            <QSTypography
              css={css(
                css`
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 400;
                  line-height: normal;
                  text-align: left;
                `,
                TextEllipsisStyles
              )}
            >
              {integrationDisplayName || integrationWabaPhoneName}
            </QSTypography>
          </Box>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            minWidth: '0',
            textOverflow: 'ellipsis',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '1',
              overflow: 'hidden',
              gap: '4px',
            }}
          >
            {messageStatusIcon}
            {!searchTermProp && senderUserName && (
              <QSTypography
                sx={{
                  overflow: 'hidden',
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#616161',
                  textOverflow: 'ellipsis',
                  lineHeight: '21px',
                }}
              >
                {senderUserName}:
              </QSTypography>
            )}
            <MessageIcon messageType={messageType} />
            <Tooltip
              enterDelay={1000}
              enterNextDelay={1000}
              title={formatText(message)}
              placement="bottom-start"
              componentsProps={{
                tooltip: {
                  sx: {
                    maxHeight: '300px',
                    overflowY: 'auto',
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: '1',
                  overflow: 'hidden',
                }}
              >
                <QSTypography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: 'inherit',
                    textAlign: 'initial',
                    flex: '1',
                    color: '#616161',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: parsedMessage,
                  }}
                ></QSTypography>
              </Box>
            </Tooltip>
          </Box>
          {!!messageTime && (
            <Box>
              <QSTypography
                sx={{
                  fontSize: '10px',
                  fontWeight: '400',
                  whiteSpace: 'nowrap',
                  color: '#2d2d2d',
                }}
              >
                {getDayFromTimestamp(new Date(messageTime).getTime())}
              </QSTypography>
            </Box>
          )}
        </Box>
      </Box>
    </ButtonBase>
  );
};

export default SearchMessageRow;
